// WORKFORCE-BOARDVIEW: the tab a board opens on. A board opened without `?view=` or `?kind=`
// opens on the person's own default for it (`me/prefs/board_view`), else the board's
// (`settings.default_kind`, set by whoever may edit the board), else its default saved view as
// before. A kind with a saved view opens that view (the default one when it is of that kind);
// a kind without one shows the default view as that kind, as the kind tabs do.
type Kind = "board" | "list" | "table" | "calendar" | "timeline" | "gallery";
type ViewLike = { id: string; kind: Kind; is_default: boolean; position: number };

type SpaceLike = {
  kind: string;
  team_id: string | null;
  owner_membership_id: string | null;
  is_private: boolean;
  settings: { member_access?: "viewer" | "editor" | undefined };
};

/**
 * Whether the caller may set the board's default (a board settings edit: `board#editor`, the
 * API's boardRole). From what the client knows: a company manager (owner, admin, operator)
 * edits every board; a personal space is its owner's; a shared space with `member_access`
 * editor makes every member an editor, `viewer` (the default) makes a non-manager a viewer. A
 * team or private space depends on team seats and space tuples the client does not read, so it
 * is offered and the server decides (an error toast when refused). Unknown space: not offered.
 */
export function canEditBoardSettings(
  who: { manager: boolean; member: boolean; membershipId: string | null },
  space: SpaceLike | undefined,
): boolean {
  if (!who.member) return false;
  if (who.manager) return true;
  if (!space) return false;
  if (space.kind === "personal")
    return who.membershipId !== null && space.owner_membership_id === who.membershipId;
  if (space.team_id !== null || space.is_private) return true;
  return space.settings.member_access === "editor";
}

/**
 * BOARD-DEFAULT-VIEW: what opening a space shows, `ordered` being its live boards in the
 * sidebar's order (`boardsOf`). An explicit `?view=overview` shows the overview; else the space's
 * `settings.opens_on` (`overview`, or one of its live boards), else its first board, else (no
 * boards) the overview. A board opens on its own default view (`openingKind`).
 */
export function spaceOpening(
  opensOn: string | null | undefined,
  ordered: readonly { id: string }[],
  explicit: "overview" | undefined,
): { board: string } | "overview" {
  if (explicit === "overview" || opensOn === "overview") return "overview";
  const picked = ordered.find((b) => b.id === opensOn) ?? ordered[0];
  return picked ? { board: picked.id } : "overview";
}

/** The person's default for this board, else the board's, else none. */
export function openingKind(
  boardId: string,
  boardDefault: Kind | null | undefined,
  mine: Partial<Record<string, Kind>> | undefined,
): Kind | null {
  return mine?.[boardId] ?? boardDefault ?? null;
}

/**
 * The view shown and the kind it is shown as (`kind` undefined: the view's own). `views` sorted
 * by position. An explicit `viewId` or `kind` from the URL always wins.
 */
export function openingView<V extends ViewLike>(
  views: V[],
  viewId: string | undefined,
  kind: Kind | undefined,
  start: Kind | null,
): { active: V | null; kind: Kind | undefined } {
  const fallback = views.find((v) => v.is_default) ?? views[0] ?? null;
  const picked = views.find((v) => v.id === viewId) ?? fallback;
  if (viewId !== undefined || kind !== undefined || !start || fallback?.kind === start)
    return { active: picked, kind };
  const ofKind = views.find((v) => v.kind === start);
  return ofKind ? { active: ofKind, kind: undefined } : { active: fallback, kind: start };
}

/**
 * BOARD-DEFAULT-VIEW: the tab marked "default" and whose default it is: the person's own, else
 * the board's, else the kind of its default saved view.
 */
export function defaultTab(
  mine: Kind | null,
  board: Kind | null,
  views: readonly ViewLike[],
): { kind: Kind; whose: "mine" | "board" | "view" } | null {
  if (mine) return { kind: mine, whose: "mine" };
  if (board) return { kind: board, whose: "board" };
  const fallback = views.find((v) => v.is_default) ?? views[0];
  return fallback ? { kind: fallback.kind, whose: "view" } : null;
}

/**
 * BOARD-DEFAULT-VIEW: a board opened without `?view=` or `?kind=` writes what it opened on into
 * the URL (replacing the entry, no new history), so a reload or a shared link keeps it. Null:
 * the URL already names it, or there is nothing to name.
 */
export function openedSearch(
  opened: { active: { id: string } | null; kind: Kind | undefined },
  viewId: string | undefined,
  kind: Kind | undefined,
): { view: string } | { kind: Kind } | null {
  if (viewId !== undefined || kind !== undefined) return null;
  if (opened.kind) return { kind: opened.kind };
  return opened.active ? { view: opened.active.id } : null;
}
